import { appClient } from 'api/apiClient';
import { buildPageQuery, type Paginated } from 'api/v1Helpers';
import type {
    ApprovePaymentRequestPayload,
    ApprovePaymentRequestResult,
    CreatePaymentRequestPayload,
    PaymentRequest,
    PaymentRequestStatus,
} from 'types/paymentRequests';

export async function createPaymentRequest(parishId: string, payload: CreatePaymentRequestPayload): Promise<PaymentRequest> {
    return appClient.post<PaymentRequest>(`parishes/${parishId}/payment-requests`, payload);
}

export async function getPaymentRequest(id: string): Promise<PaymentRequest> {
    return appClient.get<PaymentRequest>(`payment-requests/${id}`);
}

export async function listPaymentRequests(opts: {
    role: 'sender' | 'recipient';
    page?: number;
    pageSize?: number;
    status?: PaymentRequestStatus;
}): Promise<Paginated<PaymentRequest>> {
    const params = buildPageQuery(opts.page ?? 1, opts.pageSize ?? 20, {
        role: opts.role,
        status: opts.status,
    });
    return appClient.get<Paginated<PaymentRequest>>(`payment-requests?${params.toString()}`);
}

/**
 * Parish-wide invoice list (CLERK+ staff of `parishId`) — every invoice sent by any staff
 * member of the parish, not just the caller's own. A separate, new contract; does not touch
 * or route through `listPaymentRequests`, whose `role=sender|recipient` shape stays
 * user-scoped for the Mobile App.
 */
export async function listParishPaymentRequests(
    parishId: string,
    opts?: { page?: number; pageSize?: number; status?: PaymentRequestStatus }
): Promise<Paginated<PaymentRequest>> {
    const params = buildPageQuery(opts?.page ?? 1, opts?.pageSize ?? 20, {
        status: opts?.status,
    });
    return appClient.get<Paginated<PaymentRequest>>(`parishes/${parishId}/payment-requests?${params.toString()}`);
}

export async function approvePaymentRequest(id: string, payload: ApprovePaymentRequestPayload): Promise<ApprovePaymentRequestResult> {
    // Backend idempotency: Stripe key is `preq:<id>` (immutable request id). There is no client
    // `Idempotency-Key` header on this route — retries of the same request converge on one PI.
    return appClient.post<ApprovePaymentRequestResult>(`payment-requests/${id}/approve`, payload);
}

export async function declinePaymentRequest(id: string): Promise<PaymentRequest> {
    return appClient.post<PaymentRequest>(`payment-requests/${id}/decline`, {});
}

export async function cancelPaymentRequest(id: string): Promise<PaymentRequest> {
    return appClient.post<PaymentRequest>(`payment-requests/${id}/cancel`, {});
}
